import { fetchFirstname, NewMemberType } from "@/api/members";
import yup from "@/yup/yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { memberQueryOptions, useCreateMemberMutation } from '../../api/memberQueryOptions';

import { fetchCity } from "@/api/address";
import { ConflictError } from "@/api/ConflictError";
import { fetchMemberDuplicates, MemberDuplicateRequest, MemberDuplicateResponse } from "@/api/functions";
import { nextScoutIdQueryOptions } from "@/api/functionsQueryOptions";
import { MembershipType } from "@/api/memberships";
import { NoPermissionError } from "@/api/NoPermissionError";
import { fetchDetailPermissionsQueryOptions } from "@/api/permissionsQueryOptions";
import { sectionsAvailableQueryOptions } from "@/api/sectionsQueryOptions";
import { ValidationError } from "@/api/ValidationError";
import { ApiErrorComponent } from "@/components/ApiErrorComponent";
import CityAutocomplete from "@/components/CityAutocomplete";
import CountryAutocomplete from "@/components/CountryAutocomplete";
import FormDebug from "@/components/FormDebug";
import ScoregCheckbox from "@/components/mui/ScoregCheckbox";
import ScoregDatePicker from "@/components/mui/ScoregDatePicker";
import PostcodeAutocomplete from "@/components/PostcodeAutocomplete";
import SelectSection from "@/components/select/SelectSection";
import SelectSex from "@/components/select/SelectSex";
import StreetAutocomplete from "@/components/StreetAutocomplete";
import CreateMembershipRequestDialog from "@/dialog/CreateMembershipRequestDialog";
import { getOrganization } from "@/store/auth/authSlice";
import { DevTool } from "@hookform/devtools";
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import { Box, Checkbox, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { FolderOpen, PersonAdd } from "@nine-thirty-five/material-symbols-react/sharp";
import { useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { t } from 'i18next';
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSelector } from "react-redux";

type CreateMemberSearch = {
    relatedMemberUuid?: string;
}

export const Route = createFileRoute('/_auth/createMember')({
    component: CreateMemberComponent,
    validateSearch: (search: Record<string, unknown>): CreateMemberSearch => {
        // validate and parse the search params into a typed state
        return {
            relatedMemberUuid: (search.relatedMemberUuid as string) || '',
        }
    },
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
});


function CreateMemberComponent() {

    const search = Route.useSearch()
    const sessionOrganization = useSelector(getOrganization);

    const nextScoutIdQuery = useSuspenseQuery(nextScoutIdQueryOptions())
    const nextScoutId = nextScoutIdQuery.data

    const relatedMemberQuery = useSuspenseQuery(memberQueryOptions(search.relatedMemberUuid || ''))
    const relatedMember = relatedMemberQuery.data

    const [duplicates, setDuplicates] = React.useState<MemberDuplicateResponse[]>([]);

    const sectionsQuery = useSuspenseQuery(sectionsAvailableQueryOptions())
    const sections = sectionsQuery.data;

    const permissionsQuery = useSuspenseQuery(
        fetchDetailPermissionsQueryOptions('member', 'all', [
            'scoutId'
        ]),
    )
    const permissions = permissionsQuery.data

    const writeScoutId =
        permissions &&
        permissions.filter(
            (p) => p.detail === 'scoutId' && p.permissions.some((a) => a === 'write'),
        ).length > 0

    const schema = yup.object().shape({
        scoutId: yup.string().required(),
        scoutstate: yup.string().required(),
        firstname: yup.string().optional().max(30).label(t('property.member.firstname')),
        secondname: yup.string().max(30).label(t('property.member.secondname')),
        lastname: yup.string().required().max(30).label(t('property.member.lastname')),
        nickname: yup.string().max(30).label(t('property.member.nickname')),
        birthdate: yup.date().optional().nullable().max(new Date()).label(t('property.member.birthdate')),
        sex: yup.string().required().max(30).label(t('property.member.sex')),
        birthyear: yup.number().min(1800).max(new Date().getFullYear()).label(t('property.member.birthyear')),
        deathdate: yup.date().max(new Date()).label(t('property.member.dayOfDeath')),
        emailPrimary: yup.string().email().label(t('property.member.emailPrimary')),
        emailSecondary: yup.string().email().label(t('property.member.emailSecondary')),
        phoneMobile: yup.string().label(t('property.member.phoneMobile')),
        phoneLine: yup.string().label(t('property.member.phoneLine')),
        postcode: yup.string().notRequired().max(10).label(t('property.member.postcode')),
        city: yup.string().notRequired().max(100).label(t('property.member.city')),
        street: yup.string().notRequired().max(100).label(t('property.member.street')),
        country: yup.string().notRequired().max(100).label(t('property.member.country')),
        section: yup.object().shape({
            uuid: yup.string().optional().nullable(),
        }).optional(),
        relatedMember: yup.object().shape({
            uuid: yup.string(),
        }),
        relationType: yup.string(),
        copyParentData: yup.boolean(),
        duplicatesChecked: yup.boolean().oneOf([duplicates && duplicates.length > 0], t('error.createMember.duplicatesNotChecked')),
        joinDate: yup.date().optional().nullable().label(t('property.member.joinDate')),
        /*
        province: yup.string(),
        addressaddition1: yup.string(),
        addressaddition2: yup.string(),
        permissions: yup.array()
        */
    }).required();

    const member: NewMemberType = {
        scoutstate: 'MEMBER_FULL',
        scoutId: nextScoutId,
        lastname: '',
        firstname: '',
        country: t('defaultValue.member.country'),
        sex: 'MALE',
        birthdate: undefined,
        duplicatesChecked: false,
        joinDate: dayjs().toDate(),
    }

    const onSuccess = (newMembership: MembershipType) => {
        navigate({ to: "/member/$memberUuid/main", params: { memberUuid: newMembership.member.uuid || '' } })
    }
    const onError = (error: Error) => {
        if (error instanceof ConflictError) {
            enqueueSnackbar(t('error.message', { message: t('error.code.' + error.code) }), { variant: 'error' });
        } else if (error instanceof ValidationError) {
            enqueueSnackbar(t('error.message', { message: error.message }), { variant: 'error' });
        } else if (error instanceof NoPermissionError) {
            enqueueSnackbar(t('error.noPermission'), { variant: 'error' });
        } else {
            enqueueSnackbar(t('error.default'), { variant: 'error' });
        }
    }

    const { mutate: createMember } = useCreateMemberMutation(onSuccess, onError);
    const navigate = useNavigate();

    const { control, register, watch, setValue, handleSubmit, formState: { isDirty, isValid, errors }, getValues } = useForm<NewMemberType>({
        resolver: yupResolver<NewMemberType, any, any>(schema),
        mode: "onBlur",
        shouldFocusError: true,
    });

    const onSubmit = (data: NewMemberType) => {
        console.log('onSubmit')
        if (isValid) {
            createMember({ ...member, ...data, ...{ relatedMember: { uuid: relatedMember.uuid } } })
        }
    }

    React.useEffect(() => {
        const subscription = watch((value, { name }) => {

            if (name == 'firstname' || name == 'lastname' || name == 'birthdate') {
                const duplicateRequest: MemberDuplicateRequest = { lastname: value.lastname, firstname: value.firstname, birthdate: value.birthdate };
                fetchMemberDuplicates(duplicateRequest).then((duplicates) => {
                    if (duplicates) {
                        // enqueueSnackbar(t('warning.duplicatesFound', { count: duplicates.length }), { variant: 'warning' });
                        setDuplicates(duplicates)
                    }
                })
            }

            if (name == 'birthdate' || name == 'sex') {
                if (value.birthdate && value.sex) {
                    const age = dayjs().diff(value.birthdate, 'year')
                    // find suggested section
                    const filtered = sections.filter((section) => {
                        return section.sectionSex && section.minimumAge && section.maximumAge && section.sectionSex == value.sex && section.minimumAge <= age && section.maximumAge >= age
                    })
                    if (filtered.length == 1) {
                        setValue('section.uuid', filtered[0].uuid)
                    }
                }
            }

            if (name == 'firstname') {
                const sexPromise = fetchFirstname(value.firstname || '')
                sexPromise.then((sex: string) => {
                    if (sex == 'FEMALE' || sex == 'MALE') {
                        setValue('sex', sex)
                    }
                })
            }

            if (name == 'postcode') {
                if (value.postcode && value.postcode.length == 4) {
                    const cityPromise = fetchCity(value.postcode || '')
                    cityPromise.then((city: string) => {
                        if (city && city.length > 0) {
                            setValue('city', city)
                        }
                    })
                }
            }
        }
        )
        return () => subscription.unsubscribe()
    }, [watch])

    const [phoneMobileWarning, setPhoneMobileWarning] = React.useState('')
    const [phoneLineWarning, setPhoneLineWarning] = React.useState('')

    React.useEffect(() => {
        const subscription = watch((value, { name }) => {
            if (name == 'phoneMobile') {
                const regex = new RegExp('^[+]{0,1}[0-9]+[/ ]{0,1}[ 0-9]+[ -]{0,1}[ 0-9]+$')
                const valid = regex.test(value.phoneMobile || '')
                if (!valid) {
                    setPhoneMobileWarning(t('error.phoneSyntax'))
                } else {
                    setPhoneMobileWarning('')
                }
            }
            if (name == 'phoneLine') {
                const regex = new RegExp('^[+]{0,1}[0-9]+[/ ]{0,1}[ 0-9]+[ -]{0,1}[ 0-9]+$')
                const valid = regex.test(value.phoneLine || '')
                if (!valid) {
                    setPhoneLineWarning(t('error.phoneSyntax'))
                } else {
                    setPhoneLineWarning('')
                }
            }

        }
        )
        return () => subscription.unsubscribe()
    }, [watch])

    const [scoutIdEditable, setScoutIdEditable] = React.useState(false)
    const handleClickScoutIdEditable = () => setScoutIdEditable((editable) => !editable);

    const [requestDialogOpen, setRequestDialogOpen] = React.useState(false)
    const handleRequestDialogOpen = () => setRequestDialogOpen(true);
    const [membershipUuid, setMembershipUuid] = React.useState('')

    return (
        <>
            <div>
                {
                    relatedMember.uuid && <p>{t('label.createMember.relatedMember', { lastname: relatedMember.lastname, firstname: relatedMember.firstname })}</p>
                }
            </div>
            <Box sx={{ p: 1 }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={1}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField
                                defaultValue={member.scoutId} {...register("scoutId")}
                                label={t('property.member.scoutId')}
                                disabled={!scoutIdEditable}
                                required={true}
                                slotProps={writeScoutId ? {
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleClickScoutIdEditable}
                                                    edge="end"
                                                >
                                                    {scoutIdEditable ? <EditOffIcon /> : <EditIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                } : {}}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <FormControl fullWidth variant="standard">
                                <InputLabel id="job-select-label">{t('property.membership.scoutstate')}</InputLabel>
                                <Controller
                                    name="scoutstate"
                                    control={control}
                                    defaultValue={member.scoutstate}
                                    render={({ field }) => (
                                        <Select
                                            labelId="job-select-label" {...field}>
                                            <MenuItem value="MEMBER_FULL">{t('enum.scoutstate.MEMBER_FULL')}</MenuItem>
                                            <MenuItem value="MEMBER_ADD">{t('enum.scoutstate.MEMBER_ADD')}</MenuItem>
                                            <MenuItem value="CONTACT">{t('enum.scoutstate.CONTACT')}</MenuItem>
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField defaultValue={member.firstname} {...register("firstname")} label={t('property.member.firstname')} helperText={errors.firstname?.message} />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField defaultValue={relatedMember.lastname || member.lastname} {...register("lastname")} required={true} label={t('property.member.lastname')} className="scoreg-required" helperText={errors.lastname?.message} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <ScoregDatePicker name="birthdate" defaultValue={member.birthdate ? dayjs(member.birthdate) : null} control={control} label={t('property.member.birthdate')} />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <SelectSex
                                control={control}
                                name="sex"
                                defaultValue={member.sex}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            {duplicates && duplicates.length > 0 ? (
                                <><Typography>{t('label.createMember.duplicatesFound')}</Typography>
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>{t('property.member.scoutId')}</TableCell>
                                                    <TableCell>{t('property.member.lastname')}</TableCell>
                                                    <TableCell>{t('property.member.firstname')}</TableCell>
                                                <TableCell>{t('property.member.age')}</TableCell>
                                                    <TableCell>{t('property.membership.currentOrganization')}</TableCell>
                                                    <TableCell>{t('property.membership.scoutstate')}</TableCell>
                                                    <TableCell>{t('action.requestOrOpen')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {duplicates.map((dup) => {
                                                    return <TableRow key={dup.uuid}>
                                                        <TableCell component="th" scope="row">{dup.member.scoutId}</TableCell>
                                                        <TableCell>{dup.member.lastname}</TableCell>
                                                        <TableCell>{dup.member.firstname}</TableCell>
                                                    <TableCell>{dup.member.age}</TableCell>
                                                        <TableCell>{dup.organization.name}</TableCell>
                                                        <TableCell>{t('enum.scoutstate.' + dup.scoutstate)}</TableCell>
                                                        <TableCell>
                                                            {dup.uuid && dup.organization.uuid != sessionOrganization.uuid ? (
                                                                <Tooltip title={t('action.requestMembership')}>
                                                                    <IconButton onClick={() => {
                                                                        setMembershipUuid(dup.uuid)
                                                                        handleRequestDialogOpen()
                                                                    }
                                                                    }>
                                                                        <PersonAdd width={20} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            ) : <></>}
                                                            {dup.uuid && dup.organization.uuid == sessionOrganization.uuid ? (
                                                                <Tooltip title={t('action.open')}>
                                                                    <IconButton onClick={() => {
                                                                        navigate({ to: "/member/$memberUuid/main", params: { memberUuid: dup.member.uuid } })
                                                                    }
                                                                    }>
                                                                        <FolderOpen width={20} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            ) : <></>}
                                                        </TableCell>
                                                    </TableRow>
                                                })
                                                }
                                            </TableBody>
                                        </Table>
                                    </TableContainer></>) : <></>}
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 2
                            }}>
                            <CountryAutocomplete control={control} setValue={setValue} name='country' defaultValue={relatedMember.country || member.country} errors={errors} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 2
                            }}>
                            <PostcodeAutocomplete control={control} setValue={setValue} name='postcode' defaultValue={relatedMember.postcode || member.postcode} errors={errors} />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                md: 3,
                                lg: 2
                            }}>
                            <CityAutocomplete control={control} setValue={setValue} name='city' getPostcode={() => { return getValues('postcode') }} defaultValue={relatedMember.city || member.city} errors={errors} />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <StreetAutocomplete control={control} setValue={setValue} name='street' getPostcode={() => { return getValues('postcode') }} getCity={() => { return getValues('city') }} defaultValue={relatedMember.street || member.street} errors={errors} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField defaultValue={member.emailPrimary} {...register("emailPrimary")} label="E-Mail" aria-invalid={errors.emailPrimary ? "true" : "false"} error={errors.emailPrimary && true} helperText={errors.emailPrimary?.message} />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField defaultValue={member.emailSecondary} {...register("emailSecondary")} label="E-Mail 2" aria-invalid={errors.emailSecondary ? "true" : "false"} helperText={errors.emailSecondary?.message} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField defaultValue={member.phoneLine} {...register("phoneLine")}
                                onKeyDown={(e) => {
                                    if (e.code === 'Space' || e.code === 'Backspace' || e.code === 'Delete' || e.code === 'ArrowRight' || e.code === 'ArrowLeft') {
                                        return true
                                    }
                                    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                                        return true
                                    }
                                    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                                        return true
                                    }
                                    if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
                                        return true
                                    }
                                    if (!/^[-+/ 0-9]+$/.test(e.key)) {
                                        e.preventDefault()
                                    }
                                }}
                                label={t('property.member.phoneLine')} helperText={errors.phoneLine?.message || phoneLineWarning} />
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <TextField defaultValue={member.phoneMobile} {...register("phoneMobile")}
                                onKeyDown={(e) => {
                                    if (e.code === 'Space' || e.code === 'Backspace' || e.code === 'Delete' || e.code === 'ArrowRight' || e.code === 'ArrowLeft') {
                                        return true
                                    }
                                    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                                        return true
                                    }
                                    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                                        return true
                                    }
                                    if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
                                        return true
                                    }
                                    if (!/^[-+/ 0-9]+$/.test(e.key)) {
                                        e.preventDefault()
                                    }
                                }}
                                label={t('property.member.phoneMobile')} helperText={errors.phoneMobile?.message || phoneMobileWarning} />
                        </Grid>

                    </Grid>
                    <Grid container spacing={1}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <SelectSection control={control} optional={true} name="section.uuid" />
                        </Grid>
                    </Grid>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <ScoregDatePicker name="joinDate" defaultValue={member.joinDate ? dayjs(member.joinDate) : null} control={control} label={t('property.member.joinDate')} />
                    </Grid>
                </Grid>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            {duplicates && duplicates.length > 0 ? (
                                <FormControlLabel control={
                                    <Checkbox
                                        {...register("duplicatesChecked", {
                                            validate: {
                                                duplicatesChecked: () => {
                                                    if (duplicates && duplicates.length > 0) {
                                                        return t('error.createMember.duplicatesNotChecked')
                                                    }
                                                    return true
                                                }
                                            }
                                    })}
                                    onChange={(e) => {
                                        e.target.blur()
                                    }}
                                />
                                }
                                    label={t('label.createMember.duplicatesChecked')} />
                            ) : <></>}
                        </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                        {
                            relatedMember.uuid && <>
                                <Grid size={12}>{t('label.createMember.createRelation')}: </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 4
                                    }}>
                                    <FormControl fullWidth variant="standard">
                                        <InputLabel id="relation-select-label">{t('property.memberRelation.type')}</InputLabel>
                                        <Controller
                                            name="relationType"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    labelId="relation-select-label" {...field}>
                                                    <MenuItem value="FATHER">{t('enum.memberRelation.type.FATHER')}</MenuItem>
                                                    <MenuItem value="MOTHER">{t('enum.memberRelation.type.MOTHER')}</MenuItem>
                                                    <MenuItem value="WIFE">{t('enum.memberRelation.type.WIFE')}</MenuItem>
                                                    <MenuItem value="HUSBAND">{t('enum.memberRelation.type.HUSBAND')}</MenuItem>
                                                    <MenuItem value="SISTER">{t('enum.memberRelation.type.SISTER')}</MenuItem>
                                                    <MenuItem value="BROTHER">{t('enum.memberRelation.type.BROTHER')}</MenuItem>
                                                    <MenuItem value="DAUGHTER">{t('enum.memberRelation.type.DAUGHTER')}</MenuItem>
                                                    <MenuItem value="SON">{t('enum.memberRelation.type.SON')}</MenuItem>
                                                    <MenuItem value="SIBLING">{t('enum.memberRelation.type.SIBLING')}</MenuItem>
                                                    <MenuItem value="KID">{t('enum.memberRelation.type.KID')}</MenuItem>
                                                    <MenuItem value="PARTNER">{t('enum.memberRelation.type.PARTNER')}</MenuItem>
                                                    <MenuItem value="LEGAL_PARENT">{t('enum.memberRelation.type.LEGAL_PARENT')}</MenuItem>
                                                    <MenuItem value="LEGAL_KID">{t('enum.memberRelation.type.LEGAL_KID')}</MenuItem>
                                                    <MenuItem value="CONTACT">{t('enum.memberRelation.type.CONTACT')}</MenuItem>
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 6,
                                        lg: 4
                                    }}>
                                    <p>{t('property.memberRelation.of')} {relatedMember.lastname} {relatedMember.firstname}</p>
                                </Grid>
                            </>
                        }
                    </Grid>
                    <Grid container spacing={1}>
                        {
                            relatedMember.uuid && <Grid size={6}>
                                <ScoregCheckbox control={control} name="copyParentData" defaultValue={true} label={t('label.createMember.copyParentData')} />
                            </Grid>
                        }
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <Button type="submit" variant="contained" disabled={!isDirty || !isValid}>
                                {t('action.createMember')}
                            </Button>
                        </Grid>
                    </Grid>
                    <CreateMembershipRequestDialog open={requestDialogOpen} setOpen={setRequestDialogOpen} membershipUuid={membershipUuid} />

                    <Grid container spacing={2}>
                        <FormDebug dirty={isDirty} valid={isValid} errors={errors} value={member}></FormDebug>
                    </Grid>
                    {import.meta.env.VITE_DEBUG === 'true' &&
                        <DevTool control={control} />
                    }
                </form>
            </Box >
        </>
    );
}
